import { Pressable, View } from 'react-native';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Sheet } from '@/components/ui/Sheet';
import { Text } from '@/components/ui/Text';

import { PlanFeatureRow, type PlanFeatureRowProps } from './PlanFeatureRow';
import { PlanOptionCard, type PaywallPlan } from './PlanOptionCard';

const FEATURES: PlanFeatureRowProps[] = [
  { label: 'Unlimited templates', annotation: 'Free · 2' },
  { label: 'Unlimited runs', annotation: 'Free · 10' },
  { label: 'Live links & collaboration' },
  { label: 'Offline sync & sealed logs' },
];

export interface PaywallSheetProps {
  visible: boolean;
  onClose: () => void;
  plans: PaywallPlan[];
  selectedPlanId: string;
  onSelectPlan: (id: string) => void;
  trialLine?: string;
  onSubscribe: () => void;
  onRestore: () => void;
  busy?: boolean;
}

/** The full Pro gate sheet (paywall). */
export function PaywallSheet({
  visible,
  onClose,
  plans,
  selectedPlanId,
  onSelectPlan,
  trialLine,
  onSubscribe,
  onRestore,
  busy,
}: PaywallSheetProps) {
  return (
    <Sheet visible={visible} onClose={onClose}>
      <Badge label="Pro" tone="pro" className="mb-3" />
      <Text variant="title">Go unlimited with Pro</Text>
      <Text variant="subtitle" className="mt-1">
        Free covers 2 templates and 10 runs — enough to get going. Pro lifts every limit
        and keeps shared runs reliable.
      </Text>

      <View className="mt-4">
        {FEATURES.map((f) => (
          <PlanFeatureRow key={f.label} {...f} />
        ))}
      </View>

      <View className="mt-4 flex-row gap-3">
        {plans.map((plan) => (
          <PlanOptionCard
            key={plan.id}
            plan={plan}
            selected={plan.id === selectedPlanId}
            onPress={() => onSelectPlan(plan.id)}
          />
        ))}
      </View>

      {trialLine ? (
        <Text variant="caption" className="mt-3 text-center">
          {trialLine}
        </Text>
      ) : null}

      <Button
        className="mt-4"
        block
        label="Start free trial"
        busy={busy}
        onPress={onSubscribe}
      />
      <Pressable onPress={onRestore} hitSlop={8} className="mt-3 self-center">
        <Text variant="caption" className="text-muted">
          Restore purchase
        </Text>
      </Pressable>
    </Sheet>
  );
}
